import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { getProposalNotificationPrefs, updateProposalNotificationPrefs } from "../api/prospectingReportsApi";

const Toggle = ({ on, onChange }) => (
  <button type="button" onClick={() => onChange(!on)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${on ? "bg-[#2f9e6b]" : "bg-gray-300"}`}>
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${on ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

const Row = ({ label, field, prefs, set }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-gray-700">{label}</span>
    <Toggle on={!!prefs[field]} onChange={(v) => set(field, v)} />
  </div>
);

/** Proply "Proposal notifications" — per-agent toggles for views + acceptance/callback. */
const NotificationSettingsModal = ({ onClose }) => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getProposalNotificationPrefs().then(({ data }) => setPrefs(data)).catch(() => toast.error("Could not load settings."));
  }, []);

  const set = async (field, value) => {
    const next = { ...prefs, [field]: value };
    setPrefs(next);
    setSaving(true);
    try {
      await updateProposalNotificationPrefs({ [field]: value });
    } catch (e) {
      toast.error("Could not save.");
      setPrefs((p) => ({ ...p, [field]: !value }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Proposal notifications {saving && <Loader2 className="inline w-4 h-4 animate-spin text-gray-300" />}</h2>
            <p className="text-sm text-gray-500 mt-1">Get notified whenever prospects view or engage with proposals owned by you or your teammates.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {!prefs ? (
          <div className="flex justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="p-5 space-y-5">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Proposal views (email)</h3>
              <Row label="Created by me" field="views_email_created" prefs={prefs} set={set} />
              <Row label="Shared with me" field="views_email_shared" prefs={prefs} set={set} />
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Acceptance &amp; callback (email)</h3>
              <Row label="Created by me" field="accept_email_created" prefs={prefs} set={set} />
              <Row label="Shared with me" field="accept_email_shared" prefs={prefs} set={set} />
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Acceptance &amp; callback (SMS)</h3>
              <Row label="Created by me" field="accept_sms_created" prefs={prefs} set={set} />
              <Row label="Shared with me" field="accept_sms_shared" prefs={prefs} set={set} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
